// Pure logic for the consulting (ticket) tab of the PAID report.
import { EMAIL_RE } from "@/lib/quote";

// "absent": key missing (free tier / old backend) -> the tab is not shown at all.
export function consultingState(data) {
  const c = data && data.consulting;
  if (!c || typeof c !== "object") return "absent";
  return c.active ? "active" : "inactive";
}

// Options for the optional "refers to a report point" select.
export function findingOptions(findings) {
  const seen = new Set();
  const out = [];
  for (const f of Array.isArray(findings) ? findings : []) {
    const code = f && typeof f.code === "string" ? f.code.slice(0, 100) : "";
    if (!code || seen.has(code) || !f.title) continue;
    seen.add(code);
    out.push({ code, title: String(f.title) });
  }
  return out;
}

export const canReply = (ticket) => !!ticket && ticket.status !== "closed" && (ticket.messages || []).length < 20;

export function validateTicket(v, errors) {
  const out = {};
  if (!v.subject.trim()) out.subject = errors.subject;
  if (!v.message.trim()) out.message = errors.message;
  if (!EMAIL_RE.test(v.email.trim())) out.email = errors.email;
  return out;
}

export function buildTicketPayload(v) {
  const p = { subject: v.subject.trim().slice(0, 120), message: v.message.trim().slice(0, 4000), email: v.email.trim().slice(0, 200) };
  if (v.finding_code) p.finding_code = String(v.finding_code).slice(0, 100);
  return p;
}

// "Destek" tab's service checkboxes (owner request 2026-10-01) — ids must
// match backend/server.py's SUPPORT_SERVICES exactly. No price shown for
// any of these: the owner reviews the business and sends a personal quote.
export const SUPPORT_SERVICE_IDS = [
  "consulting_only", "full_service", "website_rebuild",
  "website_upgrade", "google_optimization", "ai_optimization",
];

export function validateSupportRequest(form, errors) {
  const out = {};
  if (!form.name.trim()) out.name = errors.name;
  if (!form.contact.trim()) out.contact = errors.contact;
  if (!form.services || form.services.length === 0) out.services = errors.services;
  return out;
}

export function buildSupportRequestPayload(form) {
  return { name: form.name.trim().slice(0, 200), contact: form.contact.trim().slice(0, 200), services: form.services };
}

export function formatDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Maps an axios-style error to a copy key.
export function ticketErrorKey(err) {
  const s = err && err.response && err.response.status;
  if (s === 409) return "conflict";
  if (s === 403) return "forbidden";
  if (s === 429) return "rateLimited";
  return "generic";
}

// Tab label itself lives in i18n.js's T[lang].tabs.consulting, not here.
export const CONSULTING_COPY = {
  de: {
    title: "Beratung per Ticket",
    endsAt: (d) => `Aktiv bis ${d}`,
    left: (n) => `${n} Tickets übrig`,
    replyTime: (h) => `Antwort innerhalb von ${h} Stunden (Mo–Fr). Schriftlich, keine Telefonate.`,
    newTitle: "Neue Frage stellen",
    subject: "Betreff",
    finding: "Bezug zu einem Punkt des Berichts (optional)",
    findingNone: "Kein bestimmter Punkt",
    message: "Ihre Nachricht",
    email: "E-Mail für die Antwort",
    send: "Ticket senden",
    sending: "Wird gesendet …",
    yours: "Ihre Tickets",
    none: "Noch keine Tickets.",
    loading: "Wird geladen …",
    loadFailed: "Tickets konnten nicht geladen werden.",
    you: "Sie",
    expert: "Beratung",
    status: { open: "Offen", answered: "Beantwortet", closed: "Geschlossen" },
    reply: "Antwort schreiben",
    replySend: "Antwort senden",
    closedNote: "Dieses Ticket ist geschlossen.",
    errors: { subject: "Bitte einen Betreff angeben.", message: "Bitte eine Nachricht schreiben.", email: "Bitte eine gültige E-Mail-Adresse angeben." },
    failures: {
      conflict: "Das Limit für dieses Ticket oder Ihr Kontingent ist erreicht.",
      forbidden: "Die Beratung ist für diesen Bericht nicht aktiv.",
      rateLimited: "Zu viele Versuche. Bitte später erneut versuchen.",
      generic: "Das hat nicht geklappt. Bitte später erneut versuchen.",
    },
    sent: "Ticket gesendet.",
    // Inactive state — "Destek": no-price request for one or more
    // additional services (owner request 2026-10-01). The owner reviews
    // the business in detail and sends a personal quote afterwards.
    offerTitle: "Weitere Unterstützung anfragen",
    offerIntro: "Welche dieser Leistungen interessieren Sie? Wir prüfen Ihr Unternehmen im Detail und senden Ihnen ein persönliches Angebot.",
    services: {
      consulting_only: "Nur Beratung — Sie beheben die Lücken selbst, wir begleiten Sie dabei per Ticket.",
      full_service: "Alle digitalen Lücken komplett von uns beheben lassen.",
      website_rebuild: "Website komplett neu erstellen lassen.",
      website_upgrade: "Bestehende Website überarbeiten und auf den aktuellen Stand bringen lassen.",
      google_optimization: "Google-Optimierung.",
      ai_optimization: "KI-Optimierung.",
    },
    reqName: "Name",
    reqContact: "Telefon oder E-Mail",
    reqSend: "Anfrage senden",
    reqDone: "Anfrage gesendet, wir melden uns bei Ihnen mit einem persönlichen Angebot.",
    reqFailed: "Anfrage konnte nicht gesendet werden.",
    reqErrors: {
      name: "Bitte Ihren Namen angeben.",
      contact: "Bitte Telefon oder E-Mail angeben.",
      services: "Bitte wählen Sie mindestens eine Leistung.",
    },
  },
  en: {
    title: "Consulting by ticket",
    endsAt: (d) => `Active until ${d}`,
    left: (n) => `${n} tickets left`,
    replyTime: (h) => `Reply within ${h} hours (Mon–Fri). In writing, no phone calls.`,
    newTitle: "Ask a new question",
    subject: "Subject",
    finding: "Refers to a point in the report (optional)",
    findingNone: "No specific point",
    message: "Your message",
    email: "Email for the reply",
    send: "Send ticket",
    sending: "Sending …",
    yours: "Your tickets",
    none: "No tickets yet.",
    loading: "Loading …",
    loadFailed: "Tickets could not be loaded.",
    you: "You",
    expert: "Consulting",
    status: { open: "Open", answered: "Answered", closed: "Closed" },
    reply: "Write a reply",
    replySend: "Send reply",
    closedNote: "This ticket is closed.",
    errors: { subject: "Please enter a subject.", message: "Please write a message.", email: "Please enter a valid email address." },
    failures: {
      conflict: "The limit for this ticket or your allowance has been reached.",
      forbidden: "Consulting is not active for this report.",
      rateLimited: "Too many attempts. Please try again later.",
      generic: "That did not work. Please try again later.",
    },
    sent: "Ticket sent.",
    offerTitle: "Request further support",
    offerIntro: "Which of these services interest you? We'll review your business in detail and send you a personal quote.",
    services: {
      consulting_only: "Consulting only — you close the gaps yourself, we guide you by ticket.",
      full_service: "Have us close all the digital gaps for you.",
      website_rebuild: "Have a completely new website built.",
      website_upgrade: "Have your existing website reworked and brought up to date.",
      google_optimization: "Google optimization.",
      ai_optimization: "AI optimization.",
    },
    reqName: "Name",
    reqContact: "Phone or email",
    reqSend: "Send request",
    reqDone: "Request sent, we will get back to you with a personal quote.",
    reqFailed: "The request could not be sent.",
    reqErrors: {
      name: "Please enter your name.",
      contact: "Please enter a phone number or email.",
      services: "Please select at least one service.",
    },
  },
};
